/**
 * Campaign Cards section
 * Single-select pill filtering + "Load more" pagination.
 * "All" can show cards in a fresh random order on every page load,
 * while every other filter keeps the merchant's configured sort order.
 * Supports multiple instances of the section on one page.
 */
(function () {
  'use strict';

  function initCampaignCards(root) {
    if (!root || root.dataset.campaignCardsInitialized) return;
    root.dataset.campaignCardsInitialized = 'true';

    var pageSize = parseInt(root.dataset.pageSize, 10) || 12;
    var randomizeAll = root.dataset.randomizeAll === 'true';
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-filter-pill]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    var loadMoreBtn = root.querySelector('[data-load-more]');
    var noResultsEl = root.querySelector('[data-no-results]');
    var statusEl = root.querySelector('[data-status]');
    var searchToggle = root.querySelector('[data-search-toggle]');
    var searchBar = root.querySelector('[data-search-bar]');
    var searchInput = root.querySelector('[data-search-input]');
    var searchClear = root.querySelector('[data-search-clear]');

    var state = {
      activeFilter: 'all',
      visibleLimit: pageSize,
      searchQuery: ''
    };

    // Build a one-time shuffled order for "All", generated fresh on every
    // page load. rank[originalIndex] = its position once shuffled.
    var shuffledForAll = null;
    if (randomizeAll && cards.length > 1) {
      var order = cards.map(function (_, i) { return i; });
      for (var i = order.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = order[i];
        order[i] = order[j];
        order[j] = tmp;
      }
      var rank = new Array(order.length);
      order.forEach(function (originalIndex, position) {
        rank[originalIndex] = position;
      });
      shuffledForAll = order.map(function (originalIndex) {
        return cards[originalIndex];
      });
      cards.forEach(function (card, i) {
        card.dataset.shuffleRank = rank[i];
      });
    }

    function cardTags(card) {
      return (card.dataset.tags || '')
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
    }

    function getMatches() {
      var isAll = state.activeFilter === 'all';
      var source = isAll && shuffledForAll ? shuffledForAll : cards;

      var byFilter = isAll
        ? source
        : source.filter(function (card) {
          return cardTags(card).indexOf(state.activeFilter) !== -1;
        });

      if (!state.searchQuery) return byFilter;

      return byFilter.filter(function (card) {
        var title = (card.dataset.title || '').toLowerCase();
        return title.indexOf(state.searchQuery) !== -1;
      });
    }

    function render() {
      var matches = getMatches();
      var usingShuffleOrder = state.activeFilter === 'all' && shuffledForAll;

      cards.forEach(function (card) {
        card.hidden = matches.indexOf(card) === -1;
        card.style.order = usingShuffleOrder ? card.dataset.shuffleRank : '';
      });

      matches.forEach(function (card, index) {
        card.classList.toggle('is-hidden-initial', index >= state.visibleLimit);
      });

      var shownCount = Math.min(state.visibleLimit, matches.length);

      if (loadMoreBtn) {
        loadMoreBtn.hidden = state.visibleLimit >= matches.length;
      }

      if (noResultsEl) {
        noResultsEl.hidden = matches.length !== 0;
      }

      if (statusEl) {
        statusEl.textContent = matches.length === 0
          ? 'No campaigns match this filter.'
          : 'Showing ' + shownCount + ' of ' + matches.length + ' campaigns.';
      }
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (pill.classList.contains('is-active')) return;

        pills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');

        state.activeFilter = pill.dataset.filter;
        state.visibleLimit = pageSize;
        render();
      });
    });

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        state.visibleLimit += pageSize;
        render();
      });
    }

    if (searchToggle && searchBar && searchInput) {
      // Collapsed by default (CSS animates the open/close via max-height +
      // opacity instead of an abrupt hidden-attribute toggle). `inert`
      // keeps the collapsed field out of the tab order and unclickable in
      // browsers that support it; harmless no-op otherwise.
      searchBar.inert = true;

      searchToggle.addEventListener('click', function () {
        var isOpen = searchBar.classList.contains('is-open');

        if (isOpen) {
          searchBar.classList.remove('is-open');
          searchBar.inert = true;
          searchToggle.setAttribute('aria-expanded', 'false');
          if (searchInput.value) {
            searchInput.value = '';
            state.searchQuery = '';
            state.visibleLimit = pageSize;
            render();
          }
        } else {
          searchBar.classList.add('is-open');
          searchBar.inert = false;
          searchToggle.setAttribute('aria-expanded', 'true');
          searchInput.focus();
        }
      });

      searchInput.addEventListener('input', function () {
        state.searchQuery = searchInput.value.trim().toLowerCase();
        state.visibleLimit = pageSize;
        if (searchClear) searchClear.hidden = !searchInput.value;
        render();
      });

      if (searchClear) {
        searchClear.addEventListener('click', function () {
          searchInput.value = '';
          searchClear.hidden = true;
          state.searchQuery = '';
          state.visibleLimit = pageSize;
          searchInput.focus();
          render();
        });
      }
    }

    render();
  }

  function initAll() {
    document.querySelectorAll('[data-campaign-cards]').forEach(initCampaignCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a merchant adds/edits the section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-campaign-cards]');
    if (root) {
      delete root.dataset.campaignCardsInitialized;
      initCampaignCards(root);
    }
  });
})();
